import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';

import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Badge, 
  ProgressBar, 
  Modal, 
  Form 
} from 'react-bootstrap';

// WhatsApp Asistanı veri tipi tanımı
interface WhatsAppAssistant {
  id: number;
  name: string;
  company: string;
  status: 'active' | 'inactive' | 'maintenance';
  phoneNumber: string;
  totalMessages: number;
  responseRate: number;
  avgResponseTime: string;
  lastActive: string;
  configuration: {
    welcomeMessage: string;
    businessHours: string;
    autoReply: boolean;
    language: string;
  };
  statistics: {
    totalConversations: number;
    successfulResponses: number;
    failedResponses: number;
    pendingResponses: number;
  };
}

// Örnek veri
const dummyAssistants: WhatsAppAssistant[] = [
  {
    id: 1,
    name: 'Customer Support Bot',
    company: 'Tech Solutions Inc.',
    status: 'active',
    phoneNumber: '+1 234 567 8900',
    totalMessages: 1250,
    responseRate: 98.5,
    avgResponseTime: '2.5s',
    lastActive: '2024-03-20 15:30',
    configuration: {
      welcomeMessage: 'Welcome to Tech Solutions! How can I help you today?',
      businessHours: 'Mon-Fri 9:00-18:00',
      autoReply: true,
      language: 'English',
    },
    statistics: {
      totalConversations: 450,
      successfulResponses: 440,
      failedResponses: 5,
      pendingResponses: 5,
    },
  },
  {
    id: 2,
    name: 'Sales Assistant',
    company: 'Global Retail',
    status: 'active',
    phoneNumber: '+1 234 567 8901',
    totalMessages: 850,
    responseRate: 95.2,
    avgResponseTime: '3.1s',
    lastActive: '2024-03-20 14:45',
    configuration: {
      welcomeMessage: 'Welcome to Global Retail! Looking for great deals?',
      businessHours: '24/7',
      autoReply: true,
      language: 'English, Spanish',
    },
    statistics: {
      totalConversations: 320,
      successfulResponses: 300,
      failedResponses: 12,
      pendingResponses: 8,
    },
  },
  {
    id: 3,
    name: 'Support Bot',
    company: 'Digital Services',
    status: 'maintenance',
    phoneNumber: '+1 234 567 8902',
    totalMessages: 0,
    responseRate: 0,
    avgResponseTime: 'N/A',
    lastActive: '2024-03-19 10:15',
    configuration: {
      welcomeMessage: 'Welcome to Digital Services Support!',
      businessHours: 'Mon-Sun 8:00-20:00',
      autoReply: false,
      language: 'English',
    },
    statistics: {
      totalConversations: 0,
      successfulResponses: 0,
      failedResponses: 0,
      pendingResponses: 0,
    },
  },
];

const WhatsappChatbotsPage: React.FC = () => {
  const [selectedAssistant, setSelectedAssistant] = useState<WhatsAppAssistant | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const handleConfigClick = (assistant: WhatsAppAssistant) => {
    setSelectedAssistant(assistant);
    setShowConfigModal(true);
  };

  const getStatusVariant = (status: string): string => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'danger';
      case 'maintenance':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  return (
    <Container fluid className="py-4">
      <style>{`
        .whatsapp-page-title { color: ${isDark ? '#e2e8f0' : '#0d6efd'}; }
        .whatsapp-card {
          background: ${isDark ? '#1f2937' : 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)'} !important;
          border: 1px solid ${isDark ? '#334155' : 'transparent'} !important;
          box-shadow: ${isDark ? '0 4px 16px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.08)'} !important;
          color: ${isDark ? '#e5e7eb' : 'inherit'};
        }
        .text-muted { color: ${isDark ? '#94a3b8 !important' : '#6c757d !important'}; }
        .bg-light { background-color: ${isDark ? '#0b1220 !important' : '#f8f9fa !important'}; }
        .modal-content { background: ${isDark ? '#111827' : '#ffffff'}; color: ${isDark ? '#e5e7eb' : '#212529'}; }
        .modal-header, .modal-footer { border-color: ${isDark ? '#334155' : '#dee2e6'}; }
      `}</style>
      {/* Başlık Bölümü */}
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="fw-bold whatsapp-page-title">WhatsApp Assistants</h2>
        </Col>
        <Col xs="auto">
          <Button 
            variant="primary" 
            className="d-flex align-items-center"
            style={{
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
            }}
          >
            <i className="bi bi-plus-lg me-2"></i>
            Create New Assistant
          </Button>
        </Col>
      </Row>

      {/* Asistan Kartları */}
      <Row className="g-4">
        {dummyAssistants.map(assistant => (
          <Col xs={12} md={6} lg={4} key={assistant.id}>
            <Card 
              className="h-100 border-0 shadow-sm whatsapp-card" 
              style={{
                borderRadius: '16px',
                transition: 'transform 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.transform = 'translateY(-5px)')}
              onMouseOut={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <Card.Body>
                {/* Başlık */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-bold mb-0">{assistant.name}</h5>
                  <Badge 
                    bg={getStatusVariant(assistant.status)}
                    pill
                  >
                    {assistant.status}
                  </Badge>
                </div>

                {/* Şirket Bilgisi */}
                <p className="text-muted mb-3">{assistant.company}</p>

                {/* Telefon Numarası */}
                <div className="d-flex align-items-center mb-3">
                  <div className="d-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10 me-2" style={{ width: '32px', height: '32px' }}>
                    <i className="bi bi-telephone-fill text-success"></i>
                  </div>
                  <span>{assistant.phoneNumber}</span>
                </div>

                {/* İstatistikler */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="fw-medium">Response Rate</span>
                    <span>{assistant.responseRate}%</span>
                  </div>
                  <ProgressBar 
                    now={assistant.responseRate} 
                    variant="primary" 
                    style={{ 
                      height: '8px', 
                      borderRadius: '4px',
                      background: 'rgba(0,0,0,0.1)'
                    }}
                  />
                  <small className="text-muted">Success Rate</small>
                </div>

                {/* Hızlı İstatistikler */}
                <Row className="g-2 mb-4">
                  <Col xs={6}>
                    <div className="text-center p-2 rounded" style={{ backgroundColor: 'rgba(33, 150, 243, 0.1)' }}>
                      <h5 className="text-primary mb-0">{assistant.statistics.totalConversations}</h5>
                      <small className="text-muted">Total Conversations</small>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="text-center p-2 rounded" style={{ backgroundColor: 'rgba(76, 175, 80, 0.1)' }}>
                      <h5 className="text-success mb-0">{assistant.statistics.successfulResponses}</h5>
                      <small className="text-muted">Successful</small>
                    </div>
                  </Col>
                </Row>

                {/* Aksiyon Butonları */}
                <div className="d-flex gap-2 mt-auto">
                  <Button 
                    variant="outline-primary" 
                    className="w-100 d-flex align-items-center justify-content-center"
                    onClick={() => handleConfigClick(assistant)}
                    style={{ borderRadius: '8px' }}
                  >
                    <i className="bi bi-gear-fill me-2"></i>
                    Configuration
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    className="w-100 d-flex align-items-center justify-content-center"
                    style={{ borderRadius: '8px' }}
                  >
                    <i className="bi bi-graph-up me-2"></i>
                    Analytics
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Konfigürasyon Modal */}
      <Modal 
        show={showConfigModal} 
        onHide={() => setShowConfigModal(false)}
        size="lg"
        centered
      >
        {selectedAssistant && (
          <>
            <Modal.Header closeButton>
              <Modal.Title className="d-flex align-items-center">
                <i className="bi bi-gear-fill text-primary me-2"></i>
                Assistant Configuration
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="g-4">
                <Col xs={12}>
                  <h6 className="mb-2">Basic Information</h6>
                  <Card className="bg-light border-0">
                    <Card.Body>
                      <Row>
                        <Col xs={12} md={4} className="mb-2 mb-md-0">
                          <strong>Name:</strong> {selectedAssistant.name}
                        </Col>
                        <Col xs={12} md={4} className="mb-2 mb-md-0">
                          <strong>Phone Number:</strong> {selectedAssistant.phoneNumber}
                        </Col>
                        <Col xs={12} md={4}>
                          <strong>Status:</strong>{' '}
                          <Badge bg={getStatusVariant(selectedAssistant.status)} pill>
                            {selectedAssistant.status}
                          </Badge>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xs={12}>
                  <h6 className="mb-2">Configuration Settings</h6>
                  <Card className="bg-light border-0">
                    <Card.Body>
                      <Row className="mb-2">
                        <Col xs={12}>
                          <strong>Welcome Message:</strong>
                          <p className="mb-2">{selectedAssistant.configuration.welcomeMessage}</p>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12} md={4} className="mb-2 mb-md-0">
                          <strong>Business Hours:</strong>
                          <p className="mb-0">{selectedAssistant.configuration.businessHours}</p>
                        </Col>
                        <Col xs={12} md={4} className="mb-2 mb-md-0">
                          <strong>Auto Reply:</strong>
                          <p className="mb-0">
                            {selectedAssistant.configuration.autoReply ? 
                              <Badge bg="success" pill>Enabled</Badge> : 
                              <Badge bg="danger" pill>Disabled</Badge>
                            }
                          </p>
                        </Col>
                        <Col xs={12} md={4}>
                          <strong>Language:</strong>
                          <p className="mb-0">{selectedAssistant.configuration.language}</p>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xs={12}>
                  <h6 className="mb-2">Performance Statistics</h6>
                  <Card className="bg-light border-0">
                    <Card.Body>
                      <Row className="text-center g-3">
                        <Col xs={6} md={3}>
                          <h4 className="text-primary mb-0">{selectedAssistant.statistics.totalConversations}</h4>
                          <small className="text-muted">Total Conversations</small>
                        </Col>
                        <Col xs={6} md={3}>
                          <h4 className="text-success mb-0">{selectedAssistant.statistics.successfulResponses}</h4>
                          <small className="text-muted">Successful Responses</small>
                        </Col>
                        <Col xs={6} md={3}>
                          <h4 className="text-danger mb-0">{selectedAssistant.statistics.failedResponses}</h4>
                          <small className="text-muted">Failed Responses</small>
                        </Col>
                        <Col xs={6} md={3}>
                          <h4 className="text-warning mb-0">{selectedAssistant.statistics.pendingResponses}</h4>
                          <small className="text-muted">Pending Responses</small>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowConfigModal(false)}>
                Close
              </Button>
              <Button 
                variant="primary"
                style={{
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  border: 'none',
                }}
              >
                Save Changes
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </Container>
  );
};

export default WhatsappChatbotsPage;
